import React, { useEffect, useRef, useState } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

import { Box } from 'ui/box';
import { mergeSx } from 'ui/merge-sx';

import { Checkbox } from 'ui/checkbox';

import { getUserDisplayName } from 'helpers/string.helpers';

import type { ReportUser } from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/hooks/use-report-data';

interface UserAccessChangePayload {
  userId: string | number;
  hasAccess: boolean;
}

interface ReportAssignmentUsersProps {
  reportId: string | number;
  reportUsers: ReportUser[] | undefined;
  onUserAccessChange: (
    reportId: string | number,
    userData: UserAccessChangePayload
  ) => void;
  isDisabled: boolean;
  isClosing: boolean;
}

const USER_CELL_SX = { py: 1, px: 2 } satisfies SxProps<Theme>;

const USER_CELL_DIVIDER_SX = {
  borderBottomWidth: '1px',
  borderColor: 'tokens.navy.200',
  py: 1,
  px: 2,
} satisfies SxProps<Theme>;

const ReportAssignmentUsers = ({
  reportId,
  reportUsers,
  onUserAccessChange,
  isDisabled,
  isClosing,
}: ReportAssignmentUsersProps): React.JSX.Element | null => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  useEffect(() => {
    if (!contentRef.current) return undefined;

    if (isClosing) {
      setHeight(contentRef.current.scrollHeight);
      const timer = setTimeout(() => setHeight(0), 10);

      return () => clearTimeout(timer);
    } else if (reportUsers) {
      const timer = setTimeout(
        () => contentRef.current && setHeight(contentRef.current.scrollHeight),
        10
      );

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [reportUsers, isClosing]);

  useEffect(() => {
    if (reportUsers && contentRef.current && !isClosing) {
      setHeight(contentRef.current.scrollHeight);
    }

    return () => {
      setHeight(0);
    };
  }, []);

  if (!reportUsers && !isClosing) {
    return null;
  }

  return (
    <tr>
      <td colSpan={5} className="p-0 overflow-hidden">
        <div
          className="transition-all duration-300 ease-in-out"
          style={{
            maxHeight: `${height}px`,
            opacity: height > 0 ? 1 : 0,
          }}
        >
          <Box ref={contentRef}>
            <table className="w-full">
              <tbody>
                {reportUsers &&
                  reportUsers.map((user, index) => {
                    const isLastUser = index === reportUsers.length - 1;
                    const cellSx = isLastUser
                      ? USER_CELL_SX
                      : USER_CELL_DIVIDER_SX;

                    return (
                      <tr
                        key={user.id}
                        className="transition-colors duration-200"
                      >
                        <Box
                          component="td"
                          sx={mergeSx({ width: '5%' }, cellSx)}
                        />
                        <Box
                          component="td"
                          sx={mergeSx({ width: '40%' }, cellSx)}
                        >
                          <Box sx={{ color: 'tokens.gray.700' }}>
                            {`${user.firstName} ${user.lastName}`}
                          </Box>
                          <Box
                            sx={{
                              color: 'tokens.gray.600',
                              fontSize: '0.875rem',
                              lineHeight: '1.25rem',
                            }}
                          >
                            {user.jobTitle}
                          </Box>
                        </Box>
                        <Box
                          component="td"
                          sx={mergeSx(
                            { textAlign: 'center', width: '15%' },
                            cellSx
                          )}
                        >
                          <Checkbox
                            id={`user-access-${reportId}-${user.id}`}
                            value={String(user.id)}
                            checked={!!user.hasAccess}
                            onChange={(_event, hasAccess) =>
                              onUserAccessChange(reportId, {
                                userId: String(user.id),
                                hasAccess,
                              })
                            }
                            disabled={isDisabled}
                            size="small"
                            aria-label={`Report access for ${getUserDisplayName(
                              user
                            )}`}
                            data-testid={`checkbox-user-access-${reportId}-${user.id}`}
                          />
                        </Box>
                        <Box
                          component="td"
                          sx={mergeSx({ width: '20%' }, cellSx)}
                        />
                        <Box
                          component="td"
                          sx={mergeSx({ width: '20%' }, cellSx)}
                        />
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </Box>
        </div>
      </td>
    </tr>
  );
};

export default React.memo(ReportAssignmentUsers);
